// Root security — transport and projection helpers for the platform owner tier.
//
// Root (`root_admin`) is STRICTLY ADDITIVE over SuperAdmin: a Root may do everything a SuperAdmin
// may do, and some actions additionally require Root. Nothing here removes an ordinary SuperAdmin
// capability — it only decides whether the extra, Root-only controls are drawn.
//
// The UI is NEVER the boundary. Every Root-only route re-checks server-side, so a caller who forced
// these flags on would still be refused. What this module buys is honesty: an ordinary SuperAdmin is
// not shown a control that would simply 403, and — because during an engagement the SuperAdmins on
// this plane INCLUDE the penetration testers — is not shown the external-recipient roster at all.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ROLE_ROOT = 'root_admin';
export const ROLE_SUPERADMIN = 'super_admin';

// Friendly labels. The raw key is an implementation detail and should not be shown as a role name.
export const ROLE_LABELS = {
  [ROLE_ROOT]: 'Root Administrator',
  [ROLE_SUPERADMIN]: 'Super Administrator',
};

export function roleLabel(role) {
  const key = String(role || '').trim().toLowerCase();
  return ROLE_LABELS[key] || (key ? key : 'unknown');
}

export function isRootRole(role) {
  return String(role || '').trim().toLowerCase() === ROLE_ROOT;
}

// The same token cascade the rest of the SuperAdmin plane already uses. Kept identical rather than
// "tidied" — a divergent lookup here would silently sign requests as nobody.
export function superAdminToken() {
  try {
    return (
      localStorage.getItem('superadmin_session') ||
      localStorage.getItem('superadmin_session_token') ||
      localStorage.getItem('superAdminToken') ||
      localStorage.getItem('superadminToken') ||
      ''
    );
  } catch (e) {
    return '';
  }
}

// Carries the parsed body, not just a status line: the step-up challenge lives inside `detail`, and a
// thrown `Error('HTTP 403')` would lose exactly the field the retry flow needs.
export class RootApiError extends Error {
  constructor(status, detail, body) {
    super(typeof detail === 'string' && detail ? detail : `HTTP ${status}`);
    this.name = 'RootApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

export async function rootFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const token = superAdminToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BACKEND_URL}${path}`, { credentials: 'include', ...options, headers });
  let body = null;
  try { body = await res.json(); } catch (e) { body = null; }
  if (!res.ok) throw new RootApiError(res.status, body && body.detail, body);
  return body;
}

// A step-up challenge, or null. The backend answers 403 with `detail.state = MFA_STEP_UP_REQUIRED`
// so a client can tell "prove you are present" apart from "you may not do this at all" — the second
// must never be retried with a code.
export const STEP_UP_REQUIRED = 'MFA_STEP_UP_REQUIRED';

export function stepUpChallenge(err) {
  const d = err && err.detail;
  if (d && typeof d === 'object' && d.state === STEP_UP_REQUIRED) {
    return { action: d.action || '', recency_minutes: d.recency_minutes || null };
  }
  return null;
}

// Fails closed on every uncertain path. A network blip, a 403, a mixed-revision canary that has not
// got the route yet — all of them mean "not Root", so no Root-only control is drawn and no roster is
// fetched. The opposite default would leak the roster on exactly the failures worth worrying about.
export const NO_ROOT_STATUS = Object.freeze({
  is_root: false,
  role_label: ROLE_LABELS[ROLE_SUPERADMIN],
  can_manage_staging_recipients: false,
  can_assign_root: false,
  can_manage_protected_accounts: false,
  step_up_fresh: false,
  step_up_recency_minutes: null,
});

export async function fetchRootStatus() {
  try {
    const data = await rootFetch('/api/v1/super-admin/security/root-status');
    if (!data || typeof data !== 'object') return { ...NO_ROOT_STATUS };
    // Only ever true when the server says true — never coerced from a truthy string or a missing key.
    return {
      ...NO_ROOT_STATUS,
      ...data,
      is_root: data.is_root === true,
      can_manage_staging_recipients: data.can_manage_staging_recipients === true,
      can_assign_root: data.can_assign_root === true,
      can_manage_protected_accounts: data.can_manage_protected_accounts === true,
      step_up_fresh: data.step_up_fresh === true,
    };
  } catch (e) {
    return { ...NO_ROOT_STATUS };
  }
}

// Client-side mirror of the server's exact-address rule. Advisory only: it keeps an obvious typo from
// becoming a round trip. The server re-validates, and the server is the authority.
export function isExactAddress(value) {
  const e = String(value || '').trim().toLowerCase();
  if (!e || e.includes('*') || e.includes(' ')) return false;
  if ((e.match(/@/g) || []).length !== 1) return false;
  const [local, domain] = e.split('@');
  return Boolean(local) && domain.includes('.') && !domain.startsWith('.');
}

export const MAX_EXPIRY_DAYS = 30;
export const DEFAULT_EXPIRY_DAYS = 7;

export function isValidExpiryDays(days) {
  const n = Number(days);
  return Number.isInteger(n) && n >= 1 && n <= MAX_EXPIRY_DAYS;
}

export const PURPOSE_PLATFORM_ADMIN_INVITATION = 'platform_admin_invitation';
export const PURPOSE_LABELS = {
  [PURPOSE_PLATFORM_ADMIN_INVITATION]: 'Platform Admin Invitation',
};

export function purposeLabel(purpose) {
  const key = String(purpose || '').trim();
  return PURPOSE_LABELS[key] || key || '—';
}

export const RECIPIENT_STATUS_META = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
  expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-700' },
  revoked: { label: 'Revoked', cls: 'bg-red-100 text-red-800' },
};

export function recipientStatus(status) {
  const key = String(status || '').trim().toLowerCase();
  return RECIPIENT_STATUS_META[key] || { label: key || 'unknown', cls: 'bg-gray-100 text-gray-700' };
}

export function formatTimestamp(value) {
  const s = String(value || '');
  if (!s) return '—';
  return s.slice(0, 16).replace('T', ' ');
}
